import { useContext, useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubLink,
  DevHubList,
  DevHubListItem,
  devhubMakeStyles,
  DevHubPaper,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { ApiPortalCategory, ApiSpec } from './ApiPortalTypes';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import { ApiPortalContext } from '../../contexts/ApiPortalContext';
import { slugifyHyphen } from '../../util/stringFormat';
import ApiPortalTabbedPage from './ApiPortalTabbedPage';

const useStyles = devhubMakeStyles((theme: any) => ({
  selectedCategory: {
    fontWeight: 'bold',
    fontSize: '1rem',
    color: theme.palette.primary.main,
  },
}));

/**
 * A component to render API specs for the API Portal in a catalog format.
 * @constructor
 */
const ApiPortalCatalog = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { categoryName } = useParams<{ categoryName?: string }>();
  const formattedCategoryName = categoryName
    ? categoryName
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';
  const [selectedCategory, setSelectedCategory] = useState<string>(
    formattedCategoryName || 'All Categories',
  );

  const [categories, setCategories] = useState<ApiPortalCategory[]>([]);
  const [filteredItems, setFilteredItems] = useState<
    (ApiSpec & { category: string })[]
  >([]);
  const { org, loading, error, formattedOrgName } =
    useContext(ApiPortalContext);

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
  };

  useEffect(() => {
    if (org) {
      const allCategories =
        org.categoriesPaged?.categories?.filter(
          category => category.apiSpecsPaged?.totalCount > 0,
        ) || [];
      if (categoryName) {
        setCategories(
          allCategories.filter(x => slugifyHyphen(x.name) === categoryName),
        );
      } else {
        setCategories(allCategories);
      }
    }
  }, [org, categoryName]);

  useEffect(() => {
    if (categories?.length > 0) {
      if (selectedCategory === 'All Categories') {
        setFilteredItems(
          categories.flatMap(category =>
            category.apiSpecsPaged?.specs?.map(spec => ({
              ...spec,
              category: category.name,
            })),
          ),
        );
      } else {
        const slugifiedCategory = slugifyHyphen(selectedCategory);
        setFilteredItems(
          categories
            .filter(
              category => slugifyHyphen(category.name) === slugifiedCategory,
            )
            .flatMap(category =>
              category.apiSpecsPaged?.specs?.map(spec => ({
                ...spec,
                category: category.name,
              })),
            ),
        );
      }
    }
  }, [categories, selectedCategory]);

  const handleBackClick = () => {
    if (categoryName) {
      navigate(`/api-portals/${org._normalizedName}/${categoryName}`);
    } else {
      window.history.back();
    }
  };

  useEffect(() => {
    const theme = localStorage.getItem('theme');
    if (theme === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  const renderSpecCatalog = () => {
    return (
      <DevHubGrid container spacing={2}>
        {!categoryName && (
          <DevHubGrid item xs={2}>
            <DevHubPaper fullHeight>
              <DevHubTypography variant="h4">Categories</DevHubTypography>
              <DevHubList>
                {[
                  categoryName ? [] : 'All Categories',
                  ...categories?.map(category => category.name),
                ].map(category => (
                  <DevHubListItem
                    key={category as string}
                    button
                    onClick={() => handleCategoryClick(category as string)}
                    className={
                      selectedCategory === category
                        ? classes.selectedCategory
                        : ''
                    }
                  >
                    {category}
                  </DevHubListItem>
                ))}
              </DevHubList>
            </DevHubPaper>
          </DevHubGrid>
        )}
        <DevHubGrid item xs={categoryName ? 12 : 10}>
          <DevHubPaper fullHeight>
            <DevHubTypography variant="h6">
              Showing results for '{selectedCategory}'
            </DevHubTypography>
            <DataTable value={filteredItems} paginator rows={20}>
              <Column
                field="title"
                header="Name"
                body={row => (
                  <DevHubLink
                    component={NavLink}
                    to={`/api-portals/${org._normalizedName}/${slugifyHyphen(
                      row?.category,
                    )}/specs/${slugifyHyphen(row?.title)}`}
                  >
                    {row?.title}
                  </DevHubLink>
                )}
              />
              <Column field="category" header="Category" />
            </DataTable>
          </DevHubPaper>
        </DevHubGrid>
      </DevHubGrid>
    );
  };

  if (loading) {
    return <Progress />;
  } else if (error) {
    return (
      <DevHubAlert severity="error">
        Error loading API Portal organization data: {error.message}
      </DevHubAlert>
    );
  }

  return (
    <>
      {!categoryName ? (
        <Page themeId="home">
          <Header
            title={`${formattedOrgName} Portal`}
            subtitle="Explore the APIs and Data Feeds that power our ecosystem"
          />
          <Content>
            <DevHubBox tbPadding="medium">
              <DevHubButton variant="contained" onClick={handleBackClick}>
                <DevHubDynamicIcon iconName="ArrowBackIos" />
                Back
              </DevHubButton>
            </DevHubBox>
            {renderSpecCatalog()}
          </Content>
        </Page>
      ) : (
        <ApiPortalTabbedPage
          formattedOrgName={formattedOrgName}
          apiManagementContent={renderSpecCatalog()}
          handleBackClick={handleBackClick}
        />
      )}
    </>
  );
};

export default ApiPortalCatalog;
